// src/pages/Orcamentos.jsx
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { useEmpresaId } from '../context/EmpresaContext';
import {
  collection, query, where, getDocs, doc, getDoc, addDoc, Timestamp,
  orderBy, onSnapshot, startAt, endAt, limit
} from 'firebase/firestore';
import { Trash2, Printer, X, History, Eye, Search } from 'lucide-react';
import Swal from 'sweetalert2';
import { useReactToPrint } from 'react-to-print';

/* ==================== COMPONENTES DE IMPRESSÃO ==================== */
const OrcamentoParaImprimir = React.forwardRef(({ orcamento, infoEmpresa }, ref) => {
  if (!orcamento) return null;
  const fmt = (n) => (parseFloat(n) || 0).toFixed(2);

  return (
    <div ref={ref} className="p-8 font-sans text-sm text-gray-800 bg-white">
      <header className="flex justify-between items-start pb-4 border-b-2 border-gray-200">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {infoEmpresa?.nomeFantasia || infoEmpresa?.razaoSocial || "Nome da Empresa"}
          </h1>
          <p className="text-xs">
            {(infoEmpresa?.endereco || infoEmpresa?.logradouro || 'Rua')}, {infoEmpresa?.numero || 'S/N'}
          </p>
          <p className="text-xs">
            {(infoEmpresa?.cidade || 'Cidade')} - {(infoEmpresa?.estado || infoEmpresa?.uf || 'UF')}
          </p>
          <p className="text-xs">CNPJ: {infoEmpresa?.cnpj}</p>
        </div>
        <div className="text-right">
          <h2 className="text-xl font-bold">ORÇAMENTO</h2>
          <p>Número: #{orcamento.numero}</p>
          <p>
            Data{' '}
            {orcamento.dataCriacao
              ? new Date(orcamento.dataCriacao.seconds * 1000).toLocaleDateString()
              : new Date().toLocaleDateString()}
          </p>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-4 my-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-bold mb-2 border-b pb-1">DADOS DO CLIENTE</h3>
          <p><strong>Nome:</strong> {orcamento.clienteNome}</p>
          <p><strong>Veículo:</strong> {orcamento.veiculoDescricao}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-bold mb-2 border-b pb-1">INFORMAÇÕES</h3>
          <p><strong>Validade da Proposta:</strong> 15 dias</p>
        </div>
      </section>

      <section className="my-6">
        <h3 className="font-bold mb-2">DEFEITO RECLAMADO</h3>
        <p className="p-4 bg-gray-50 rounded-lg min-h-[50px]">
          {orcamento.defeitoReclamado || "Nenhum defeito informado."}
        </p>
      </section>

      <section className="my-6">
        <h3 className="font-bold mb-2">PEÇAS / PRODUTOS</h3>
        <table className="w-full text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2">Item</th>
              <th className="p-2 text-center">Qtd.</th>
              <th className="p-2 text-right">Valor Unit.</th>
              <th className="p-2 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {orcamento.produtos?.map((item, i) => (
              <tr key={item.id || i} className="border-b">
                <td className="p-2">{item.nome}</td>
                <td className="p-2 text-center">{item.quantidade}</td>
                <td className="p-2 text-right">R$ {fmt(item.valor)}</td>
                <td className="p-2 text-right">R$ {fmt(item.subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="my-6">
        <h3 className="font-bold mb-2">MÃO DE OBRA / SERVIÇOS</h3>
        <table className="w-full text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2">Serviço</th>
              <th className="p-2 text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {orcamento.servicos?.map((item, i) => (
              <tr key={item.id || i} className="border-b">
                <td className="p-2">{item.nome}</td>
                <td className="p-2 text-right">R$ {fmt(item.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <footer className="mt-8 pt-4 border-t-2 text-right">
        <p><strong>Subtotal Peças:</strong> R$ {fmt(orcamento.totalProdutos)}</p>
        <p><strong>Subtotal Serviços:</strong> R$ {fmt(orcamento.totalServicos)}</p>
        <h2 className="text-2xl font-bold mt-2">VALOR TOTAL: R$ {fmt(orcamento.valorTotal)}</h2>
      </footer>
    </div>
  );
});

const PrintModal = ({ orcamento, infoEmpresa, onClose }) => {
  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    content: () => printRef.current,
    documentTitle: `Orcamento-${orcamento?.numero || 'novo'}`,
    onBeforeGetContent: () => new Promise((r) => setTimeout(r, 250)),
  });

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-start py-10 z-50 overflow-y-auto">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-2xl relative my-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">Pré-visualização do Orçamento</h2>
          <div>
            <button
              onClick={handlePrint}
              disabled={!orcamento}
              className="bg-blue-600 text-white px-4 py-2 rounded-md mr-2 hover:bg-blue-700 disabled:opacity-60"
            >
              Imprimir
            </button>
            <button onClick={onClose} className="text-gray-600 p-2 rounded-full hover:bg-gray-200">
              <X />
            </button>
          </div>
        </div>

        <OrcamentoParaImprimir ref={printRef} orcamento={orcamento} infoEmpresa={infoEmpresa} />
      </div>
    </div>
  );
};

/* ==================== PÁGINA ==================== */
export default function Orcamentos() {
  const empresaId = useEmpresaId();
  const navigate = useNavigate();

  const [infoEmpresa, setInfoEmpresa] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [historicoOrcamentos, setHistoricoOrcamentos] = useState([]);

  // form
  const [clienteId, setClienteId] = useState('');
  const [veiculoId, setVeiculoId] = useState('');
  const [defeitoReclamado, setDefeitoReclamado] = useState('');
  const [itensOrcamento, setItensOrcamento] = useState([]);

  // busca produtos/serviços
  const [buscaProduto, setBuscaProduto] = useState('');
  const [buscaServico, setBuscaServico] = useState('');
  const [resultadosBuscaProdutos, setResultadosBuscaProdutos] = useState([]);
  const [buscandoProdutos, setBuscandoProdutos] = useState(false);

  // UI
  const [loading, setLoading] = useState(true);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [orcamentoSelecionado, setOrcamentoSelecionado] = useState(null);

  // helper p/ normalizar (ignora acentos/caixa)
  const normalize = useCallback((s) =>
    (s ?? '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''), []);

  /* -------- LOAD dentro de empresas/{empresaId}/... -------- */
  useEffect(() => {
    if (!empresaId) { setLoading(false); return; }
    setLoading(true);

    // Empresa
    getDoc(doc(db, 'empresas', empresaId))
      .then(s => s.exists() && setInfoEmpresa({ id: s.id, ...s.data() }))
      .catch(() => {});

    // Clientes
    const unsubClientes = onSnapshot(
      collection(db, 'empresas', empresaId, 'clientes'),
      (snap) => setClientes(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    // Produtos (stream base para fallback/local)
    const unsubProdutos = onSnapshot(
      collection(db, 'empresas', empresaId, 'produtos'),
      (snap) => setProdutos(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    // Serviços
    const unsubServicos = onSnapshot(
      collection(db, 'empresas', empresaId, 'servicos'),
      (snap) => setServicos(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    // Histórico
    getDocs(query(collection(db, 'empresas', empresaId, 'orcamentos'), orderBy('dataCriacao', 'desc')))
      .then(s => setHistoricoOrcamentos(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .finally(() => setLoading(false));

    return () => { unsubClientes(); unsubProdutos(); unsubServicos(); };
  }, [empresaId]);

  /* -------- Veículos do cliente selecionado -------- */
  useEffect(() => {
    if (!clienteId || !empresaId) { setVeiculos([]); setVeiculoId(''); return; }
    (async () => {
      try {
        const ref = collection(db, 'empresas', empresaId, 'clientes', clienteId, 'veiculos');
        const snap = await getDocs(ref);
        setVeiculos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('Erro ao buscar veículos:', e);
        setVeiculos([]);
      }
    })();
  }, [clienteId, empresaId]);

  /* -------- Totais -------- */
  const totais = useMemo(() => {
    const totalProdutos = itensOrcamento
      .filter(i => i.tipo === 'produto')
      .reduce((acc, i) => acc + (i.subtotal || 0), 0);
    const totalServicos = itensOrcamento
      .filter(i => i.tipo === 'servico')
      .reduce((acc, i) => acc + (i.valor || 0), 0);
    return { totalProdutos, totalServicos, valorTotal: totalProdutos + totalServicos };
  }, [itensOrcamento]);

  /* -------- Busca Firestore para PRODUTOS (prefixo em nomeSearch) -------- */
  useEffect(() => {
    if (!empresaId) return;

    const termRaw = buscaProduto.trim();
    if (termRaw.length < 2) { setResultadosBuscaProdutos([]); return; }

    const term = normalize(termRaw);
    let cancelled = false;
    setBuscandoProdutos(true);

    const doSearch = async () => {
      try {
        const ref = collection(db, 'empresas', empresaId, 'produtos');

        // 1) prefix search por nomeSearch (insensível a acento/caixa)
        const qNome = query(
          ref,
          orderBy('nomeSearch'),
          startAt(term),
          endAt(term + '\uf8ff'),
          limit(50)
        );

        // 2) buscas exatas por códigos (opcional)
        const extras = [];
        if (termRaw.length >= 3) {
          extras.push(getDocs(query(ref, where('sku', '==', termRaw))));
          extras.push(getDocs(query(ref, where('codigoBarras', '==', termRaw))));
          extras.push(getDocs(query(ref, where('codigo', '==', termRaw))));
        }

        const snaps = await Promise.all([getDocs(qNome), ...extras]);

        // merge sem duplicar
        const map = new Map();
        snaps.forEach(snap => snap.forEach(d => map.set(d.id, { id: d.id, ...d.data() })));

        if (!cancelled) setResultadosBuscaProdutos(Array.from(map.values()));
      } catch (e) {
        console.error('Busca produtos:', e);
        if (!cancelled) setResultadosBuscaProdutos([]);
      } finally {
        if (!cancelled) setBuscandoProdutos(false);
      }
    };

    const id = setTimeout(doSearch, 300); // debounce
    return () => { clearTimeout(id); cancelled = true; };
  }, [empresaId, buscaProduto, normalize]);

  /* -------- Filtros locais (fallback) -------- */
  const produtosLocalFiltrados = useMemo(() => {
    const t = normalize(buscaProduto.trim());
    if (!t) return produtos;
    return produtos.filter(p => {
      const nome = normalize(p.nome);
      const sku = normalize(String(p.sku ?? ''));
      const codigo = normalize(String(p.codigo ?? ''));
      const barras = normalize(String(p.codigoBarras ?? p.codBarras ?? ''));
      return nome.includes(t) || sku.includes(t) || codigo.includes(t) || barras.includes(t);
    });
  }, [produtos, buscaProduto, normalize]);

  const produtosExibidos = buscaProduto.trim().length >= 2 ? resultadosBuscaProdutos : produtosLocalFiltrados;

  const servicosFiltrados = useMemo(() => {
    const t = normalize(buscaServico.trim());
    if (!t) return servicos;
    return servicos.filter(s => normalize(s.nome).includes(t));
  }, [servicos, buscaServico, normalize]);

  /* -------- Add/Update/Remove itens -------- */
  const handleAddItem = useCallback((item, tipo) => {
    const precoProduto = item.precoVenda ?? item.valor ?? 0;
    const precoServico = item.valor ?? item.valorSugerido ?? 0;
    const valor = parseFloat(tipo === 'produto' ? precoProduto : precoServico) || 0;

    const newItem = {
      ...item,
      valor,
      tipo,
      quantidade: tipo === 'produto' ? 1 : undefined,
      subtotal: tipo === 'produto' ? valor : undefined,
    };
    setItensOrcamento(prev => [...prev, newItem]);
  }, []);

  const handleRemoveItem = useCallback((index) => {
    setItensOrcamento(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpdateQuantidade = useCallback((index, quantidade) => {
    const qtd = parseInt(quantidade, 10);
    if (isNaN(qtd) || qtd < 1) return;
    setItensOrcamento(prev => prev.map((item, i) =>
      i === index ? { ...item, quantidade: qtd, subtotal: item.valor * qtd } : item
    ));
  }, []);

  /* -------- Salvar e imprimir -------- */
  const handleSalvarEGerarOrcamento = async () => {
    if (!clienteId || !veiculoId || itensOrcamento.length === 0) {
      Swal.fire('Atenção!', 'Selecione um cliente, um veículo e adicione pelo menos um item.', 'warning');
      return;
    }

    const cliente = clientes.find(c => c.id === clienteId);
    const veiculo = veiculos.find(v => v.id === veiculoId);

    const produtosParaSalvar = itensOrcamento.filter(i => i.tipo === 'produto').map(p => ({
      id: p.id, nome: p.nome, quantidade: p.quantidade, valor: p.valor, subtotal: p.subtotal
    }));

    const servicosParaSalvar = itensOrcamento.filter(i => i.tipo === 'servico').map(s => ({
      id: s.id, nome: s.nome, valor: s.valor
    }));

    const dados = {
      numero: Date.now().toString().slice(-6),
      clienteId,
      veiculoId,
      clienteNome: cliente?.nome || 'N/A',
      veiculoDescricao: `${veiculo?.modelo || 'N/A'} - ${veiculo?.placa || 'N/A'}`,
      defeitoReclamado,
      produtos: produtosParaSalvar,
      servicos: servicosParaSalvar,
      ...totais,
      dataCriacao: Timestamp.now(),
      dataExpiracao: Timestamp.fromDate(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)),
    };

    try {
      const ref = await addDoc(collection(db, 'empresas', empresaId, 'orcamentos'), dados);
      const novo = { id: ref.id, ...dados };
      setHistoricoOrcamentos(prev => [novo, ...prev]);
      setOrcamentoSelecionado(novo);
      setShowPrintModal(true);

      // limpa form
      setClienteId(''); setVeiculoId(''); setDefeitoReclamado(''); setItensOrcamento([]);
    } catch (e) {
      console.error('Erro ao adicionar orçamento:', e);
      Swal.fire('Erro', 'Não foi possível salvar o orçamento.', 'error');
    }
  };

  const handleVisualizarOrcamento = (orc) => {
    setOrcamentoSelecionado(orc);
    setShowPrintModal(true);
  };

  /* -------- UI -------- */
  return (
    <div className="p-4 sm:p-6 md:p-8">
      <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8">Novo Orçamento</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Cliente/Veículo */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">Dados do Cliente</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <select
                value={clienteId}
                onChange={(e) => setClienteId(e.target.value)}
                className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              >
                <option value="">Selecione um cliente</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>

              <select
                value={veiculoId}
                onChange={(e) => setVeiculoId(e.target.value)}
                className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                disabled={!clienteId || veiculos.length === 0}
              >
                <option value="">
                  {veiculos.length > 0
                    ? 'Selecione um veículo'
                    : (clienteId ? 'Cliente sem veículos' : 'Selecione um cliente')}
                </option>
                {veiculos.map(v => <option key={v.id} value={v.id}>{v.modelo} - {v.placa}</option>)}
              </select>
            </div>
          </div>

          {/* Observações */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
              Defeito Reclamado / Observações
            </h2>
            <textarea
              value={defeitoReclamado}
              onChange={(e) => setDefeitoReclamado(e.target.value)}
              rows="3"
              className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              placeholder="Descreva o problema relatado pelo cliente..."
            />
          </div>

          {/* Itens */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">Adicionar Peças e Serviços</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Produtos */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300">Peças / Produtos</h3>
                  <span className="text-xs text-gray-500">{produtosExibidos.length}/{produtos.length}</span>
                </div>

                {/* Campo de busca */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={buscaProduto}
                    onChange={(e) => setBuscaProduto(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && produtosExibidos.length > 0) {
                        handleAddItem(produtosExibidos[0], 'produto');
                        setBuscaProduto('');
                      }
                    }}
                    placeholder="Pesquisar por nome, código, SKU ou barras..."
                    className="w-full pl-9 pr-8 py-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  />
                  {buscaProduto && (
                    <button
                      type="button"
                      onClick={() => setBuscaProduto('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Limpar busca"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto p-1">
                  {buscandoProdutos && (
                    <p className="text-xs text-gray-500 px-1 py-2">Buscando...</p>
                  )}
                  {!buscandoProdutos && produtosExibidos.length === 0 && (
                    <p className="text-xs text-gray-500 px-1 py-2">
                      {buscaProduto.trim().length >= 2 ? 'Nenhum produto encontrado.' : 'Digite ao menos 2 letras para buscar.'}
                    </p>
                  )}
                  {produtosExibidos.map(prod => {
                    const preco = prod.precoVenda ?? prod.valor ?? 0;
                    return (
                      <button
                        key={prod.id}
                        onClick={() => handleAddItem(prod, 'produto')}
                        className="w-full flex items-center gap-3 p-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                      >
                        <span className="flex-1 min-w-0 pr-2 truncate" title={prod.nome}>
                          {prod.nome}
                        </span>
                        <span className="shrink-0 whitespace-nowrap font-mono tabular-nums text-gray-600 dark:text-gray-400">
                          R$ {(parseFloat(preco) || 0).toFixed(2)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Serviços (filtro local) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300">Mão de Obra / Serviços</h3>
                  <span className="text-xs text-gray-500">{servicosFiltrados.length}/{servicos.length}</span>
                </div>

                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={buscaServico}
                    onChange={(e) => setBuscaServico(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && servicosFiltrados.length > 0) {
                        handleAddItem(servicosFiltrados[0], 'servico');
                        setBuscaServico('');
                      }
                    }}
                    placeholder="Pesquisar serviço por nome..."
                    className="w-full pl-9 pr-8 py-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  />
                  {buscaServico && (
                    <button
                      type="button"
                      onClick={() => setBuscaServico('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Limpar busca"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto p-1">
                  {servicosFiltrados.length === 0 && (
                    <p className="text-xs text-gray-500 px-1 py-2">Nenhum serviço encontrado.</p>
                  )}
                  {servicosFiltrados.map(serv => {
                    const preco = serv.valor ?? serv.valorSugerido ?? 0;
                    return (
                      <button
                        key={serv.id}
                        onClick={() => handleAddItem(serv, 'servico')}
                        className="w-full flex items-center gap-3 p-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                      >
                        <span className="flex-1 min-w-0 pr-2 truncate" title={serv.nome}>
                          {serv.nome}
                        </span>
                        <span className="shrink-0 whitespace-nowrap font-mono tabular-nums text-gray-600 dark:text-gray-400">
                          R$ {(parseFloat(preco) || 0).toFixed(2)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resumo */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg sticky top-8">
            <h2 className="text-xl font-semibold mb-4 border-b pb-3 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100">
              Resumo do Orçamento
            </h2>
            <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
              {itensOrcamento.length === 0 && <p className="text-gray-500 text-center py-4">Nenhum item adicionado.</p>}

              {itensOrcamento.map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex-grow min-w-0">
                    <p className="font-semibold text-gray-800 dark:text-gray-200 truncate">{item.nome}</p>
                    <p className="text-xs text-gray-500">{item.tipo === 'produto' ? 'Peça/Produto' : 'Serviço'}</p>
                  </div>

                  {item.tipo === 'produto' && (
                    <input
                      type="number"
                      min="1"
                      value={item.quantidade}
                      onChange={(e) => handleUpdateQuantidade(index, e.target.value)}
                      className="w-14 mx-2 p-1 text-center border rounded dark:bg-gray-600 dark:border-gray-500"
                    />
                  )}

                  <p className="font-mono w-24 text-right text-gray-700 dark:text-gray-300">
                    R$ {(item.subtotal ?? item.valor).toFixed(2)}
                  </p>

                  <button onClick={() => handleRemoveItem(index)} className="ml-2 text-red-500 hover:text-red-700">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t mt-4 pt-4 space-y-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
              <div className="flex justify-between"><span>Subtotal Peças:</span><span className="font-medium">R$ {totais.totalProdutos.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Subtotal Serviços:</span><span className="font-medium">R$ {totais.totalServicos.toFixed(2)}</span></div>
              <div className="flex justify-between text-xl font-bold mt-2 text-gray-900 dark:text-white">
                <span>TOTAL:</span><span>R$ {totais.valorTotal.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={handleSalvarEGerarOrcamento}
                className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={!clienteId || !veiculoId || itensOrcamento.length === 0}
              >
                <Printer size={20} className="mr-2" /> Salvar e Gerar Orçamento
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Histórico */}
      <div className="mt-12">
        <div className="flex items-center mb-4">
          <History className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-3" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Histórico de Orçamentos</h2>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 sm:p-6">
          <div className="max-h-96 overflow-y-auto pr-2 space-y-4">
            {loading && <p className="text-center text-gray-500 py-4">A carregar histórico...</p>}
            {!loading && historicoOrcamentos.length === 0 && <p className="text-center text-gray-500 py-4">Nenhum orçamento encontrado.</p>}

            {historicoOrcamentos.map(orc => (
              <div key={orc.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex-grow min-w-[150px]">
                  <p className="font-bold text-gray-800 dark:text-gray-200">#{orc.numero}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{orc.clienteNome}</p>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <p>Data: {orc.dataCriacao?.seconds ? new Date(orc.dataCriacao.seconds * 1000).toLocaleDateString() : 'N/A'}</p>
                  <p>Expira: {orc.dataExpiracao?.seconds ? new Date(orc.dataExpiracao.seconds * 1000).toLocaleDateString() : 'N/A'}</p>
                </div>
                <p className="font-bold text-lg text-gray-800 dark:text-gray-200">R$ {(orc.valorTotal || 0).toFixed(2)}</p>
                <button
                  onClick={() => handleVisualizarOrcamento(orc)}
                  className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 px-3 py-1 rounded-md text-sm font-semibold hover:bg-blue-200 dark:hover:bg-blue-900 flex items-center"
                >
                  <Eye size={16} className="mr-2" /> Visualizar
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showPrintModal && (
        <PrintModal
          orcamento={orcamentoSelecionado}
          infoEmpresa={infoEmpresa}
          onClose={() => setShowPrintModal(false)}
        />
      )}
    </div>
  );
}
